import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  PhotoCamera as PhotoIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

export interface ChecklistItem {
  _id?: string;
  name: string;
  description?: string;
  isChecked: boolean;
  checkedBy?: string | { _id: string; firstName?: string; lastName?: string };
  checkedAt?: string;
  imageUrl?: string; // Reference/instruction image S3 key (set during permit creation)
  imageDownloadUrl?: string; // Presigned download URL for imageUrl (from backend)
  evidenceImage?: string; // Evidence image S3 key (set when marking item as complete)
  evidenceImageDownloadUrl?: string; // Presigned download URL for evidenceImage (from backend)
}

export interface ChecklistSectionData {
  _id?: string;
  name: string;
  description?: string;
  isMandatory?: boolean;
  items: ChecklistItem[];
}

interface ChecklistSectionProps {
  sections: ChecklistSectionData[];
  onMarkItem?: (sectionId: string, itemId: string, evidenceImageUrl?: string) => Promise<void>;
  onUnmarkItem?: (sectionId: string, itemId: string) => Promise<void>;
  onGetUploadUrl?: (contentType: string) => Promise<{
    presignedPost: { url: string; fields: Record<string, string> };
    key: string;
  }>;
  readOnly?: boolean;
  isAssignedWorker?: boolean;
  permitStatus?: string;
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

export const ChecklistSection: React.FC<ChecklistSectionProps> = ({
  sections,
  onMarkItem,
  onUnmarkItem,
  onGetUploadUrl,
  readOnly = false,
  isAssignedWorker = false,
  permitStatus,
}) => {
  const [loadingItems, setLoadingItems] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [pendingMark, setPendingMark] = useState<{ sectionId: string; itemId: string } | null>(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewImageTitle, setPreviewImageTitle] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if user can interact with checklist
  const canInteract = !readOnly && isAssignedWorker &&
    (permitStatus === 'approved' || permitStatus === 'aprobado' || permitStatus === 'in_progress');

  const getItemKey = (sectionId: string, itemId: string) => `${sectionId}-${itemId}`;

  const handleItemClick = async (section: ChecklistSectionData, item: ChecklistItem) => {
    if (!canInteract) return;

    const sectionId = section._id || '';
    const itemId = item._id || '';
    const key = getItemKey(sectionId, itemId);

    if (item.isChecked) {
      // Unmark item
      if (!onUnmarkItem) return;

      setLoadingItems(prev => ({ ...prev, [key]: true }));
      setError(null);

      try {
        await onUnmarkItem(sectionId, itemId);
      } catch (err: any) {
        setError(err.message || 'Error al desmarcar el item');
      } finally {
        setLoadingItems(prev => ({ ...prev, [key]: false }));
      }
    } else {
      // Show dialog to optionally add evidence before marking
      setPendingMark({ sectionId, itemId });
      setEvidenceDialogOpen(true);
    }
  };

  const handleMarkWithoutEvidence = async () => {
    if (!pendingMark || !onMarkItem) return;

    const key = getItemKey(pendingMark.sectionId, pendingMark.itemId);
    setLoadingItems(prev => ({ ...prev, [key]: true }));
    setError(null);
    setEvidenceDialogOpen(false);

    try {
      await onMarkItem(pendingMark.sectionId, pendingMark.itemId);
    } catch (err: any) {
      setError(err.message || 'Error al marcar el item');
    } finally {
      setLoadingItems(prev => ({ ...prev, [key]: false }));
      setPendingMark(null);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !pendingMark || !onMarkItem || !onGetUploadUrl) return;

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Solo se permiten imágenes JPEG, PNG o WebP');
      return;
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE) {
      setError('La imagen no puede exceder 5MB');
      return;
    }

    const key = getItemKey(pendingMark.sectionId, pendingMark.itemId);
    setUploadingEvidence(true);
    setError(null);

    try {
      // Get presigned URL
      const { presignedPost, key: imageKey } = await onGetUploadUrl(file.type);

      // Upload to S3
      const formData = new FormData();
      Object.entries(presignedPost.fields).forEach(([field, value]) => {
        formData.append(field, value);
      });
      formData.append('file', file);

      const s3Response = await fetch(presignedPost.url, {
        method: 'POST',
        body: formData,
      });

      if (!s3Response.ok) {
        throw new Error('Error al subir la imagen');
      }

      setEvidenceDialogOpen(false);
      setLoadingItems(prev => ({ ...prev, [key]: true }));

      // Mark item with evidence S3 key (backend generates presigned URL for display)
      await onMarkItem(pendingMark.sectionId, pendingMark.itemId, imageKey);
    } catch (err: any) {
      setError(err.message || 'Error al subir la evidencia');
    } finally {
      setUploadingEvidence(false);
      setLoadingItems(prev => ({ ...prev, [key]: false }));
      setPendingMark(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCloseEvidenceDialog = () => {
    setEvidenceDialogOpen(false);
    setPendingMark(null);
    setError(null);
  };

  const handleViewImage = (imageUrl: string, title: string = 'Imagen') => {
    setPreviewImage(imageUrl);
    setPreviewImageTitle(title);
  };

  const getCheckedByName = (checkedBy: ChecklistItem['checkedBy']) => {
    if (!checkedBy) return 'Usuario';
    if (typeof checkedBy === 'string') return 'Usuario';
    return `${checkedBy.firstName || ''} ${checkedBy.lastName || ''}`.trim() || 'Usuario';
  };

  const getProgress = () => {
    let total = 0;
    let completed = 0;
    sections.forEach(section => {
      section.items.forEach(item => {
        total++;
        if (item.isChecked) completed++;
      });
    });
    return { total, completed, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  const progress = getProgress();

  return (
    <Box>
      {/* Header with progress */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Checklist de Seguridad
        </Typography>
        <Chip
          label={`${progress.completed}/${progress.total} (${progress.percentage}%)`}
          color={progress.percentage === 100 ? 'success' : 'warning'}
          size="small"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canInteract && !readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Solo los trabajadores asignados pueden marcar el checklist cuando el permiso está aprobado.
        </Alert>
      )}

      {/* Sections */}
      {sections.map((section, sectionIndex) => (
        <Box key={section._id || sectionIndex} sx={{ mb: sectionIndex < sections.length - 1 ? 3 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {section.name}
            </Typography>
            {section.isMandatory !== false && (
              <Chip label="Obligatorio" size="small" color="error" variant="outlined" />
            )}
          </Box>
          {section.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {section.description}
            </Typography>
          )}
          <List dense>
            {section.items.map((item, itemIndex) => {
              const itemKey = getItemKey(section._id || '', item._id || '');
              const isLoading = loadingItems[itemKey];

              return (
                <Box key={item._id || itemIndex}>
                  <ListItem
                    sx={{
                      py: 0.5,
                      borderBottom: (!item.imageDownloadUrl && !item.evidenceImageDownloadUrl && itemIndex < section.items.length - 1) ? '1px solid' : 'none',
                      borderColor: 'divider',
                      cursor: canInteract ? 'pointer' : 'default',
                      '&:hover': canInteract ? {
                        bgcolor: 'action.hover',
                      } : {},
                    }}
                    onClick={() => !isLoading && handleItemClick(section, item)}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {isLoading ? (
                        <CircularProgress size={24} />
                      ) : item.isChecked ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <UncheckedIcon color={canInteract ? 'primary' : 'disabled'} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            textDecoration: item.isChecked ? 'line-through' : 'none',
                            color: item.isChecked ? 'text.secondary' : 'text.primary',
                          }}
                        >
                          {item.name}
                        </Typography>
                      }
                      secondary={
                        <>
                          {item.description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.description}
                            </Typography>
                          )}
                          {item.isChecked && item.checkedBy && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Marcado por: {getCheckedByName(item.checkedBy)}
                              {item.checkedAt && ` - ${new Date(item.checkedAt).toLocaleString('es-CR')}`}
                            </Typography>
                          )}
                        </>
                      }
                    />
                    {/* Instruction image icon (set during permit creation) */}
                    {item.imageDownloadUrl && (
                      <Tooltip title="Ver imagen de referencia">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewImage(item.imageDownloadUrl!, 'Imagen de Referencia');
                          }}
                          sx={{ color: 'info.main' }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Evidence image icon (set when marking item) */}
                    {item.evidenceImageDownloadUrl && (
                      <Tooltip title="Ver evidencia">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewImage(item.evidenceImageDownloadUrl!, 'Evidencia');
                          }}
                        >
                          <ImageIcon color="primary" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItem>
                </Box>
              );
            })}
          </List>
        </Box>
      ))}

      {/* Evidence upload dialog */}
      <Dialog open={evidenceDialogOpen} onClose={handleCloseEvidenceDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Marcar Item</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ¿Desea agregar una foto de evidencia antes de marcar este item?
          </Typography>

          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            aria-label="Subir archivo"
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {uploadingEvidence && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEvidenceDialog} disabled={uploadingEvidence}>
            Cancelar
          </Button>
          <Button
            variant="outlined"
            startIcon={<PhotoIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingEvidence}
          >
            Agregar Foto
          </Button>
          <Button
            variant="contained"
            onClick={handleMarkWithoutEvidence}
            disabled={uploadingEvidence}
          >
            Marcar sin foto
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {previewImageTitle}
          <IconButton onClick={() => setPreviewImage(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewImage && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={previewImage}
                alt={previewImageTitle}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
